import { useRouteMatch, Link, useLocation } from 'react-router-dom';
import {
  Flex,
  Heading,
  Card,
  Text,
  Button,
  Icon,
  Link as RadixLink,
  Callout,
} from '@scale/scaleui-radix';
import {
  faArrowLeft,
  faArrowUpRightFromSquare,
  faTriangleExclamation,
} from '@fortawesome/pro-regular-svg-icons';
import { Spinner } from '@scale/scaleui';
import {
  useGetSessionsForCollection,
  collectAttendance,
  useGetLiveSession,
  useGetSessionsForCollectionWithHydratedUsers,
} from '../api';
import WebinarError from './WebinarError';
import WebinarDetailNavigation from './WebinarDetailNavigation';
import WebinarDetailSession from './WebinarDetailSession';
import WebinarDetailInvitees from './WebinarDetailInvitees';
import { isPastSession } from '../helpers';
import moment from 'moment-timezone';
import CopyButton from 'react-scale/components/WebinarSessions/components/CopyButton';
import { useMutation, useQueryClient } from '@tanstack/react-query-v4';
import { toast } from 'react-toastify';
import { EndPreviousSessionModal } from 'react-scale/components/WebinarSessions/components/EndPreviousSessionModal';
import { useToggle } from 'react-use-v13';
import { ExistingMissionsDialog } from 'react-scale/components/WebinarSessions/components/ExistingMissionsDialog';
import ClaimHostsCallout from './ClaimHostsCallout';

const LiveMeetingConflictCallout = ({
  sessionId,
  onClick,
}: {
  sessionId: string;
  onClick: () => void;
}) => {
  const { data: liveSession, isLoading: isLiveSessionLoading } = useGetLiveSession({
    sessionId,
  });

  if (isLiveSessionLoading) {
    return null;
  }

  if (liveSession?._id === sessionId) {
    return null;
  }

  return (
    <Callout.Root size="1" color="warning" className="flex flex-row justify-between gap-2">
      <Flex direction="row" gap="2" align="center" justify="center">
        <Callout.Icon>
          <Icon icon={faTriangleExclamation} size="2" label="Warning" />
        </Callout.Icon>

        <Callout.Text>
          The previous session on the Zoom account may still be in progress. If you cannot start
          your webinar, end the previous session.
        </Callout.Text>
      </Flex>

      <Flex align="center" justify="center" height="100%">
        <Button size="1" variant="outline" color="neutral" onClick={onClick}>
          End previous session
        </Button>
      </Flex>
    </Callout.Root>
  );
};

const AttendanceSyncingCallout = () => (
  <Callout.Root size="1" color="warning">
    <Callout.Icon>
      <Icon icon={faTriangleExclamation} size="2" label="Warning" />
    </Callout.Icon>
    <Callout.Text>Attendance is still in the process of being collected.</Callout.Text>
  </Callout.Root>
);

const CanceledSessionCallout = () => (
  <Callout.Root size="1" color="error">
    <Callout.Icon>
      <Icon icon={faTriangleExclamation} size="2" label="Warning" />
    </Callout.Icon>
    <Callout.Text>This webinar session is canceled and no longer available.</Callout.Text>
  </Callout.Root>
);

const RunAttendanceSyncCallout = ({
  sessionId,
  collectionId,
}: {
  sessionId: string;
  collectionId: string;
}) => {
  const queryClient = useQueryClient();
  const { mutate: runAttendanceSync, isLoading: isRunningAttendanceSync } = useMutation({
    mutationFn: () => collectAttendance(sessionId),
    onSuccess: () => {
      toast.success('Attendance collected successfully!');
      queryClient.invalidateQueries({
        queryKey: ['useGetSessionsForCollection', collectionId],
      });
      queryClient.invalidateQueries({
        queryKey: ['useGetSessionsForCollectionWithHydratedUsers', collectionId],
      });
    },
  });

  return (
    <Callout.Root size="1" color="warning" className="flex flex-row justify-between gap-2">
      <Flex direction="row" gap="2">
        <Callout.Icon>
          <Icon icon={faTriangleExclamation} size="2" label="Warning" />
        </Callout.Icon>

        <Callout.Text>
          This webinar session has exceeded the scheduled duration and attendance has not been
          collected.
        </Callout.Text>
      </Flex>

      <Button
        size="1"
        variant="outline"
        color="neutral"
        onClick={() => {
          runAttendanceSync();
        }}
        loading={isRunningAttendanceSync}
      >
        Collect attendance
      </Button>
    </Callout.Root>
  );
};

// eslint-disable-next-line complexity
const WebinarDetailContent: React.FC<{ id: string }> = ({ id }) => {
  const { data, isLoading, error } = useGetSessionsForCollection({ collectionId: id });
  const [showEndPreviousSessionModal, toggleEndPreviousSessionModal] = useToggle(false);
  const [showMissionConfigsDialog, toggleMissionConfigsDialog] = useToggle(false);

  const match = useRouteMatch();
  const webinarSessionsUrl = match.url.split(id)[0].replace(/\/$/, '');

  const { search } = useLocation();

  const isRemoAdminOrOutlierAdmin =
    window.location.pathname.includes('remoadmin') ||
    window.location.pathname.includes('outlieradmin');

  const {
    data: dataWithHydratedUsers,
    isLoading: isLoadingWithHydratedUsers,
    refetch: refetchWithHydratedUsers,
  } = useGetSessionsForCollectionWithHydratedUsers({ collectionId: id });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full w-full">
        <Spinner className="m-4" size={'lg'} />
      </div>
    );
  }

  if (error) {
    return <WebinarError error={error} />;
  }

  if (!data) {
    return <WebinarError error={new Error('No data found')} />;
  }

  const collection = data.sessions.find(session => {
    return session._id === id;
  });

  if (!collection) {
    return <WebinarError error={new Error(`No collection found for id ${id}`)} />;
  }

  const params = new URLSearchParams(search);
  const selectedSessionId = params.get('session') || data.sessions[0]._id;

  const session = data.sessions.find(session => session._id === selectedSessionId);
  if (!session) {
    return <WebinarError error={new Error(`No session found for id ${selectedSessionId}`)} />;
  }

  const elapsed = isPastSession(session);
  const attendanceCollected = elapsed && session.attendanceSyncedAt !== undefined;

  const eventStart = moment(session.eventStart);
  const eventEnd = moment(session.eventStart).add(session.duration, 'minutes');
  const minutesElapsed = moment().diff(eventEnd, 'minutes');
  const eventInProgress = moment().isBetween(eventStart, eventEnd);

  const sessionRequiresAttendanceSync = elapsed && !attendanceCollected && !session.canceled;
  const attendanceSyncing = sessionRequiresAttendanceSync && minutesElapsed < 120;
  const attendanceDidNotSync = sessionRequiresAttendanceSync && minutesElapsed >= 120;

  return (
    <div className="flex flex-col gap-4 p-4 h-full overflow-hidden">
      {!elapsed && <ClaimHostsCallout />}
      <div>
        <Button variant="ghost" asChild>
          <Link to={webinarSessionsUrl}>
            <Icon icon={faArrowLeft} label="back" />
            <Text>Back to webinar sessions</Text>
          </Link>
        </Button>
      </div>
      <Flex direction="column" gap="2">
        <Flex align="center" justify="between" wrap="wrap" gapY="2">
          <Flex align="center" gap="2" style={{ flex: '1 0 auto' }}>
            <Heading size="5">{collection.name}</Heading>
            <Flex align="center" gap="1">
              <Text size="2" color="neutral">
                {collection._id}
              </Text>
              <CopyButton text={collection._id} label="Webinar ID" size="2" />
            </Flex>
          </Flex>
          <Flex justify="end" align="center" gap="2" style={{ flex: '0 0 max-content' }}>
            {!isRemoAdminOrOutlierAdmin && (
              <Button variant="outline" color="neutral" asChild>
                <RadixLink
                  color="neutral"
                  href={`/corp/mission-config/new?webinar=${collection._id}`}
                  target="_blank"
                >
                  Add mission
                  <Icon icon={faArrowUpRightFromSquare} label="" />
                </RadixLink>
              </Button>
            )}
            {!isRemoAdminOrOutlierAdmin && (
              <Button variant="outline" color="neutral" onClick={toggleMissionConfigsDialog}>
                View missions
              </Button>
            )}
            <Button variant="outline" color="neutral" asChild>
              <Link to={`${webinarSessionsUrl}/${collection._id}/edit`}>Edit</Link>
            </Button>
          </Flex>
        </Flex>
        <Text size="2" color="neutral">
          {collection.description}
        </Text>
      </Flex>
      <Flex
        gap="4"
        style={{ flex: 1, overflow: 'hidden' }}
        direction={{
          initial: 'column',
          lg: 'row',
        }}
      >
        <Card variant="surface" className="p-0">
          <WebinarDetailNavigation sessions={data.sessions} selectedSessionId={selectedSessionId} />
        </Card>
        <Flex direction="column" gap="4" style={{ flex: 1, overflow: 'auto' }} key={session._id}>
          {attendanceSyncing && <AttendanceSyncingCallout />}
          {attendanceDidNotSync && (
            <RunAttendanceSyncCallout sessionId={session._id} collectionId={collection._id} />
          )}
          {session.canceled && <CanceledSessionCallout />}
          {!session.canceled && eventInProgress && (
            <LiveMeetingConflictCallout
              sessionId={session._id}
              onClick={toggleEndPreviousSessionModal}
            />
          )}
          <WebinarDetailSession session={session} />
          <WebinarDetailInvitees
            session={dataWithHydratedUsers?.sessions.find(s => s._id === session._id)}
            collectionId={collection._id}
            isLoading={isLoadingWithHydratedUsers}
            refetch={refetchWithHydratedUsers}
          />
        </Flex>
      </Flex>
      {showEndPreviousSessionModal && (
        <EndPreviousSessionModal toggleModal={toggleEndPreviousSessionModal} sessions={[session]} />
      )}
      {showMissionConfigsDialog && (
        <ExistingMissionsDialog
          collectionId={collection._id}
          toggleModal={toggleMissionConfigsDialog}
        />
      )}
    </div>
  );
};

const WebinarDetailPage: React.FC = () => {
  const { params } = useRouteMatch<{ id: string }>();

  return (
    <Flex direction="column" width="100%" height="100%" style={{ overflow: 'hidden' }}>
      <WebinarDetailContent id={params.id} />
    </Flex>
  );
};

export default WebinarDetailPage;
